<template>
    <div class="grid">
        <div class="col-12">
            <Toast />
            <ConfirmDialog />

            <div class="card">
                <div class="flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h5 class="m-0">Horarios</h5>
                    <Tag v-if="editingSchedule && dirty" severity="warning" value="Sin guardar" />
                    <Tag v-else-if="savedNote" severity="success" :value="savedNote" />
                </div>

                <div v-if="loaded && !schedules.length && !draft" class="empty-state">
                    <span class="empty-icon"><i class="pi pi-clock"></i></span>
                    <span class="empty-title">Este local todavía no tiene horario</span>
                    <span class="text-600">Tus meseros pueden entrar a cualquier hora.</span>
                    <Button label="Crear el horario del local" icon="pi pi-plus" class="mt-2" @click="startNew" />
                </div>

                <template v-if="draft">
                    <div class="tabs" role="tablist" aria-label="Horarios del local">
                        <button v-for="s in schedules" :key="s.id" type="button" role="tab" class="tab"
                                :class="{ active: draft.id === s.id }" :aria-selected="draft.id === s.id"
                                :disabled="dirty && draft.id !== s.id"
                                v-tooltip.bottom="dirty && draft.id !== s.id ? 'Guarda o descarta los cambios primero' : null"
                                @click="select(s)">
                            {{ s.name }}<span v-if="s.isDefault" class="tab-tag">del local</span>
                        </button>
                        <button v-if="!draft.id" type="button" role="tab" class="tab active" aria-selected="true">
                            {{ draft.name || 'Nuevo horario' }}
                        </button>
                        <button v-else type="button" class="tab tab-new" :disabled="dirty" @click="startNew">
                            <i class="pi pi-plus"></i> Nuevo horario
                        </button>
                    </div>

                    <div class="flex flex-wrap align-items-end gap-3 mt-3">
                        <div class="field m-0">
                            <label for="schedule-name" class="block mb-1">Nombre</label>
                            <InputText id="schedule-name" v-model="draft.name" maxlength="80" class="name-input"
                                       :class="{ 'p-invalid': serverError && serverError.field === 'name' }" />
                        </div>
                        <div v-if="!isDefaultSaved" class="flex align-items-center gap-2 pb-2">
                            <Checkbox inputId="schedule-default" v-model="draft.isDefault" :binary="true" />
                            <label for="schedule-default">Es el horario del local</label>
                        </div>
                    </div>

                    <div class="grid mt-3">
                        <div class="col-12 xl:col-6">
                            <div class="day-row day-head" aria-hidden="true">
                                <span>DÍA</span><span>ABIERTO</span><span>TURNOS</span>
                            </div>
                            <div v-for="(day, d) in draft.days" :key="d" class="day-row">
                                <span class="day-name">{{ DAYS[d] }}</span>
                                <span class="pt-1">
                                    <InputSwitch :modelValue="day.length > 0" :aria-label="DAYS[d] + ' abierto'"
                                                 @update:modelValue="toggleDay(d, $event)" />
                                </span>
                                <div class="shifts">
                                    <div v-if="!day.length" class="closed-day">
                                        <span class="text-600">Cerrado</span>
                                        <small v-if="carryNote(d)" class="text-600">{{ carryNote(d) }}</small>
                                    </div>
                                    <div v-for="(shift, i) in day" :key="i" class="shift">
                                        <InputMask v-model="shift.start" mask="99:99" slotChar="hh:mm" :autoClear="false"
                                                   class="time-input" :aria-label="DAYS[d] + ', turno ' + (i + 1) + ', desde'" />
                                        <span class="text-600" aria-hidden="true">→</span>
                                        <InputMask v-model="shift.end" mask="99:99" slotChar="hh:mm" :autoClear="false"
                                                   class="time-input" :aria-label="DAYS[d] + ', turno ' + (i + 1) + ', hasta'" />
                                        <span v-if="shiftNote(d, shift)" class="shift-note">{{ shiftNote(d, shift) }}</span>
                                        <Button v-if="day.length > 1" icon="pi pi-times"
                                                class="p-button-text p-button-rounded p-button-secondary p-button-sm"
                                                :aria-label="'Quitar el turno de las ' + shift.start + ' del ' + DAYS[d].toLowerCase()"
                                                @click="removeShift(d, i)" />
                                    </div>
                                    <Button v-if="day.length" label="turno" icon="pi pi-plus"
                                            class="p-button-text p-button-sm add-shift"
                                            :aria-label="'Agregar un turno el ' + DAYS[d].toLowerCase()"
                                            @click="addShift(d)" />
                                </div>
                            </div>
                        </div>

                        <div class="col-12 xl:col-6">
                            <div class="flex justify-content-between align-items-baseline flex-wrap gap-2 mb-2">
                                <span class="font-semibold text-900">Así queda la semana</span>
                                <span class="now-status">
                                    <span class="dot" :class="'dot-' + nowStatus.kind" aria-hidden="true"></span>
                                    Ahora: {{ nowStatus.label }}
                                </span>
                            </div>
                            <div class="week-row" aria-hidden="true">
                                <span></span>
                                <div class="axis">
                                    <span style="left: 0">00:00</span><span style="left: 25%">06:00</span>
                                    <span style="left: 50%">12:00</span><span style="left: 75%">18:00</span>
                                    <span style="right: 0">24:00</span>
                                </div>
                            </div>
                            <div v-for="(row, d) in weekRows" :key="row.name" class="week-row">
                                <span class="week-day" :class="{ today: d === now.weekday }">{{ row.name }}</span>
                                <div>
                                    <div class="track" role="img" :aria-label="row.name + ': ' + row.text">
                                        <span v-for="(seg, k) in row.segments" :key="k" class="seg"
                                              :class="'seg-' + seg.kind" :style="{ left: seg.left, width: seg.width }"></span>
                                        <span v-if="d === now.weekday" class="now-line" :style="{ left: nowLeft }"></span>
                                    </div>
                                    <small class="week-text">{{ row.text }}</small>
                                </div>
                            </div>
                            <div class="legend">
                                <span><span class="sw sw-shift"></span>Turno</span>
                                <span><span class="sw sw-carry"></span>Madrugada del día anterior</span>
                                <span><span class="sw sw-grace"></span>Gracia para cerrar mesas</span>
                                <span><span class="sw sw-now"></span>Ahora</span>
                            </div>
                        </div>
                    </div>

                    <Message v-if="problem" severity="error" :closable="false">{{ problem }}</Message>

                    <div class="editor-footer">
                        <div class="flex align-items-center gap-2">
                            <label for="grace">Gracia para cerrar mesas</label>
                            <InputNumber inputId="grace" v-model="draft.graceMinutes" :min="0" :max="120" :step="5"
                                         suffix=" min" inputClass="grace-input"
                                         :class="{ 'p-invalid': serverError && serverError.field === 'graceMinutes' }" />
                        </div>
                        <div class="flex align-items-center flex-wrap gap-2">
                            <Button v-if="canDelete" label="Borrar horario" icon="pi pi-trash"
                                    class="p-button-text p-button-danger" @click="confirmDelete" />
                            <Button v-if="dirty || !draft.id" label="Descartar" class="p-button-text" @click="discard" />
                            <Button label="Guardar horario" icon="pi pi-check" :loading="saving"
                                    :disabled="!canSave" @click="save" />
                        </div>
                    </div>
                </template>
            </div>

            <div v-if="loaded && schedules.length" class="card">
                <h5 class="mt-0">Quién trabaja con cada horario</h5>
                <DataTable :value="people" dataKey="userId" responsiveLayout="scroll">
                    <Column field="username" header="Usuario" />
                    <Column header="Rol" :style="{ width: '150px' }">
                        <template #body="{ data }">
                            <Tag :severity="data.role === 'Administrador' ? 'info' : 'warning'" :value="data.role" />
                        </template>
                    </Column>
                    <Column header="Horario" :style="{ minWidth: '220px' }">
                        <template #body="{ data }">
                            <Dropdown v-if="data.role !== 'Administrador'" :modelValue="assignmentOf(data)"
                                      :options="assignmentOptions" optionLabel="label" optionValue="value"
                                      :aria-label="'Horario de ' + data.username" class="w-full md:w-15rem"
                                      @change="assign(data, $event.value)" />
                            <span v-else class="text-600">Sin restricción</span>
                        </template>
                    </Column>
                    <Column header="Ahora">
                        <template #body="{ data }">
                            <span class="now-status">
                                <span class="dot" :class="'dot-' + personTone(data)" aria-hidden="true"></span>{{ data.now.label }}
                            </span>
                        </template>
                    </Column>
                </DataTable>
            </div>

            <div v-if="loaded" class="card">
                <div class="flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h5 class="m-0">Feriados y habilitaciones</h5>
                    <Button label="Nueva excepción" icon="pi pi-plus" :disabled="!schedules.length && !operators.length"
                            @click="openException" />
                </div>
                <DataTable :value="exceptions" dataKey="id" responsiveLayout="scroll">
                    <template #empty>Sin feriados ni habilitaciones próximas.</template>
                    <Column header="Tipo" :style="{ width: '130px' }">
                        <template #body="{ data }">
                            <Tag :severity="data.kind === 'CLOSED' ? 'danger' : 'info'"
                                 :value="data.kind === 'CLOSED' ? 'Cerrado' : 'Habilitado'" />
                        </template>
                    </Column>
                    <Column header="Para">
                        <template #body="{ data }">{{ data.scheduleName || data.username }}</template>
                    </Column>
                    <Column field="startsAtLabel" header="Desde" />
                    <Column field="endsAtLabel" header="Hasta" />
                    <Column field="reason" header="Motivo" />
                    <Column :style="{ width: '64px' }">
                        <template #body="{ data }">
                            <Button icon="pi pi-trash" class="p-button-text p-button-rounded p-button-secondary"
                                    :aria-label="'Quitar la excepción del ' + data.startsAtLabel"
                                    @click="confirmDeleteException(data)" />
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>

        <Dialog v-model:visible="exceptionDialog" header="Nueva excepción" :modal="true"
                :style="{ width: '480px' }" :breakpoints="{ '640px': '92vw' }">
            <div class="field">
                <SelectButton v-model="exceptionForm.kind" :options="kindOptions" optionLabel="label"
                              optionValue="value" :allowEmpty="false" aria-label="Tipo de excepción" />
            </div>
            <div class="field">
                <label for="exception-scope" class="block mb-1">Para</label>
                <Dropdown inputId="exception-scope" v-model="exceptionForm.scope" :options="scopeOptions"
                          optionLabel="label" optionValue="value" class="w-full" />
            </div>
            <div class="grid">
                <div class="col-12 sm:col-6 field mb-0">
                    <label for="exception-from" class="block mb-1">Desde</label>
                    <Calendar inputId="exception-from" v-model="exceptionForm.startsAt" :showTime="true" hourFormat="24"
                              dateFormat="dd/mm/yy" :showIcon="true" class="w-full" />
                </div>
                <div class="col-12 sm:col-6 field mb-0">
                    <label for="exception-to" class="block mb-1">Hasta</label>
                    <Calendar inputId="exception-to" v-model="exceptionForm.endsAt" :showTime="true" hourFormat="24"
                              dateFormat="dd/mm/yy" :showIcon="true" class="w-full" />
                </div>
            </div>
            <div class="field mt-3">
                <label for="exception-reason" class="block mb-1">Motivo</label>
                <InputText id="exception-reason" v-model="exceptionForm.reason" maxlength="200" class="w-full" />
            </div>
            <div v-if="exceptionPreview" class="exception-preview">
                <i :class="exceptionForm.kind === 'CLOSED' ? 'pi pi-minus-circle' : 'pi pi-check-circle'"></i>
                <span>{{ exceptionPreview }}</span>
            </div>
            <Message v-if="exceptionError" severity="error" :closable="false">{{ exceptionError }}</Message>
            <template #footer>
                <Button label="Cancelar" class="p-button-text" @click="exceptionDialog = false" />
                <Button label="Guardar" icon="pi pi-check" :loading="savingException" @click="saveException" />
            </template>
        </Dialog>
    </div>
</template>

<script>
import httpRequest from '../../service/httpRequest';
import {
    DAYS, SHORT_DAYS, apiDateTime, blocksFromDays, carryNote, daysFromBlocks, draftError, ecuadorNow, nowStatus,
    shiftNote, weekRows,
} from '../../service/workSchedule';

const UNRESTRICTED = 'libre';
const LOCAL = 'local';          // el del local: PrimeVue toma `null` como «sin elegir»

// Horarios de los meseros (F5 del cliente móvil, D-7): el dueño decide cuándo entran a la app del
// teléfono, con el resumen de la semana a la vista antes de guardar. El servidor valida todo otra vez.
export default {
    name: 'WorkSchedules',
    data() {
        return {
            DAYS,
            loaded: false,
            schedules: [],
            people: [],
            exceptions: [],
            draft: null,              // el horario en edición: {id, name, isDefault, graceMinutes, days}
            original: null,           // cómo estaba al abrirlo, para saber si hay cambios
            serverError: null,        // {message, field} del último rechazo del servidor
            saving: false,
            savedNote: null,
            now: ecuadorNow(),
            nowTimer: null,
            exceptionDialog: false,
            exceptionForm: { kind: 'CLOSED', scope: 'all', startsAt: null, endsAt: null, reason: '' },
            exceptionError: null,
            savingException: false,
            kindOptions: [
                { label: 'Cerrar', value: 'CLOSED' },
                { label: 'Habilitar fuera de horario', value: 'OPEN' },
            ],
        };
    },
    created() {
        this.load();
    },
    mounted() {
        this.nowTimer = setInterval(() => { this.now = ecuadorNow(); }, 30000);
    },
    beforeUnmount() {
        clearInterval(this.nowTimer);
    },
    computed: {
        editingSchedule() {
            return this.draft !== null;
        },
        dirty() {
            return this.draft !== null && this.snapshot(this.draft) !== this.original;
        },
        clientError() {
            return this.draft ? draftError(this.draft.days) : null;
        },
        problem() {
            return this.clientError || (this.serverError && this.serverError.message);
        },
        canSave() {
            return !!this.draft && !this.clientError && !!this.draft.name.trim() && (this.dirty || !this.draft.id);
        },
        isDefaultSaved() {
            const saved = this.schedules.find((s) => s.id === (this.draft && this.draft.id));
            return !!saved && saved.isDefault;
        },
        canDelete() {
            if (!this.draft || !this.draft.id) return false;
            return !this.isDefaultSaved || this.schedules.length === 1;
        },
        weekRows() {
            return this.draft ? weekRows(this.draft.days, this.draft.graceMinutes) : [];
        },
        nowStatus() {
            return this.draft ? nowStatus(this.draft.days, this.draft.graceMinutes, this.now) : { kind: 'none', label: '' };
        },
        nowLeft() {
            return (this.now.minutes / 1440 * 100).toFixed(3) + '%';
        },
        operators() {
            return this.people.filter((p) => p.role !== 'Administrador');
        },
        assignmentOptions() {
            return [
                { label: 'Horario del local', value: LOCAL },
                ...this.schedules.filter((s) => !s.isDefault).map((s) => ({ label: s.name, value: s.id })),
                { label: 'Sin restricción', value: UNRESTRICTED },
            ];
        },
        scopeOptions() {
            return [
                { label: 'Todo el local', value: 'all' },
                ...(this.schedules.length > 1 ? this.schedules.map((s) => ({ label: s.name, value: 's:' + s.id })) : []),
                ...this.operators.map((p) => ({ label: p.username, value: 'u:' + p.userId })),
            ];
        },
        exceptionPreview() {
            const f = this.exceptionForm;
            if (!(f.startsAt instanceof Date) || !(f.endsAt instanceof Date)) return '';
            const span = 'de ' + this.dateLabel(f.startsAt) + ' a ' + this.dateLabel(f.endsAt);
            const option = this.scopeOptions.find((o) => o.value === f.scope);
            if (f.scope === 'all') return f.kind === 'CLOSED' ? 'Nadie entra ' + span : 'Todos pueden entrar ' + span;
            if (!option) return '';
            if (String(f.scope).startsWith('s:')) {
                return (f.kind === 'CLOSED' ? 'Quienes usan «' + option.label + '» no entran ' : 'Quienes usan «' + option.label + '» pueden entrar ') + span;
            }
            return option.label + (f.kind === 'CLOSED' ? ' no entra ' : ' puede entrar ') + span;
        },
    },
    watch: {
        draft: {
            deep: true,
            handler() {
                if (this.dirty) this.savedNote = null;
            },
        },
    },
    methods: {
        carryNote(d) {
            return carryNote(this.draft.days, d);
        },
        shiftNote(d, shift) {
            return shiftNote(d, shift);
        },
        snapshot(draft) {
            return JSON.stringify({ name: draft.name.trim(), isDefault: !!draft.isDefault,
                                    graceMinutes: draft.graceMinutes, blocks: blocksFromDays(draft.days) });
        },
        edit(draft) {
            this.draft = draft;
            this.original = this.snapshot(draft);
            this.serverError = null;
        },
        select(schedule) {
            if (this.dirty && this.draft.id !== schedule.id) return;
            this.edit({ id: schedule.id, name: schedule.name, isDefault: schedule.isDefault,
                        graceMinutes: schedule.graceMinutes, days: daysFromBlocks(schedule.blocks) });
        },
        startNew() {
            const first = !this.schedules.length;
            this.edit({ id: null, name: first ? 'Horario del local' : '', isDefault: first, graceMinutes: 30,
                        days: DAYS.map(() => []) });
            this.savedNote = null;
        },
        discard() {
            const saved = this.schedules.find((s) => s.id === this.draft.id)
                || this.schedules.find((s) => s.isDefault) || this.schedules[0];
            if (saved) this.select(saved);
            else this.draft = null;
        },
        toggleDay(d, open) {
            if (!open) {
                this.draft.days[d] = [];
                return;
            }
            // Un día que se abre arranca como el último día abierto: casi siempre es el mismo turno.
            const template = [...this.draft.days].reverse().find((day) => day.length);
            this.draft.days[d] = template ? template.map((s) => ({ ...s })) : [{ start: '09:00', end: '17:00' }];
        },
        addShift(d) {
            this.draft.days[d].push({ start: '12:00', end: '15:00' });
        },
        removeShift(d, i) {
            this.draft.days[d].splice(i, 1);
        },
        // Lo que manda el servidor (la pantalla completa) reemplaza lo que se mostraba. `select` es el
        // horario a abrir después de guardar o borrar; sin él, un borrador en curso no se toca.
        apply(data, select) {
            this.schedules = data.schedules || [];
            this.people = data.people || [];
            this.exceptions = data.exceptions || [];
            this.loaded = true;
            if (select === undefined) return;
            const schedule = this.schedules.find((s) => s.id === select)
                || this.schedules.find((s) => s.isDefault) || this.schedules[0];
            if (schedule) this.select(schedule);
            else this.draft = null;
        },
        async load() {
            const response = await httpRequest.getWorkSchedules();
            if (response.status === 200) this.apply(response.data, null);
            else this.fail(response);
        },
        fail(response) {
            if (response.status === 403) {
                this.$toast.add({ severity: 'error', summary: 'Sin permiso', detail: 'Tu usuario no puede administrar los horarios.', life: 5000 });
            } else if (response.status === 422 && response.error) {
                this.$toast.add({ severity: 'warn', summary: 'Horarios', detail: response.error, life: 8000 });
            } else {
                this.$toast.add({ severity: 'error', summary: 'Error interno', detail: 'Hemos detectado un error, por favor intente nuevamente.', life: 4000 });
            }
        },
        pushNote(push) {
            if (!push || !push.applied) return 'Guardado · los teléfonos lo verán en un minuto';
            if (!push.phones) return 'Guardado';
            return 'Guardado · ' + push.phones + (push.phones === 1 ? ' teléfono al día' : ' teléfonos al día');
        },
        async save() {
            if (!this.canSave) return;
            this.saving = true;
            this.serverError = null;
            const response = await httpRequest.saveWorkSchedule({
                id: this.draft.id, name: this.draft.name.trim(), isDefault: !!this.draft.isDefault,
                graceMinutes: this.draft.graceMinutes, blocks: blocksFromDays(this.draft.days),
            });
            this.saving = false;
            if (response.status === 200) {
                this.apply(response.data, response.data.scheduleId);
                this.savedNote = this.pushNote(response.data.push);
            } else if (response.status === 422 && response.error) {
                this.serverError = { message: response.error, field: response.data && response.data.field };
            } else {
                this.fail(response);
            }
        },
        confirmDelete() {
            this.$confirm.require({
                header: 'Borrar horario',
                message: 'Quienes usan «' + this.draft.name + '» pasan al horario del local.',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Borrar',
                rejectLabel: 'Cancelar',
                acceptClass: 'p-button-danger',
                accept: () => this.remove(),
            });
        },
        async remove() {
            const response = await httpRequest.deleteWorkSchedule(this.draft.id);
            if (response.status === 200) {
                this.apply(response.data, null);
                this.savedNote = null;
            } else {
                this.fail(response);
            }
        },
        assignmentOf(person) {
            return person.unrestricted ? UNRESTRICTED : (person.scheduleId || LOCAL);
        },
        personTone(person) {
            const state = person.now && person.now.state;
            return state === 'OPEN' ? 'open' : state === 'GRACE' ? 'grace' : state === 'UNRESTRICTED' ? 'free' : 'closed';
        },
        async assign(person, value) {
            const change = value === UNRESTRICTED
                ? { userId: person.userId, unrestricted: true }
                : { userId: person.userId, scheduleId: value === LOCAL ? null : value };
            const response = await httpRequest.assignWorkSchedule(change);
            if (response.status === 200) {
                this.apply(response.data);
                this.$toast.add({ severity: 'success', summary: 'Horario asignado', detail: person.username, life: 2500 });
            } else {
                this.fail(response);
                const fresh = await httpRequest.getWorkSchedules();
                if (fresh.status === 200) this.apply(fresh.data);
            }
        },
        dateLabel(date) {
            const pad = (n) => String(n).padStart(2, '0');
            return SHORT_DAYS[(date.getDay() + 6) % 7] + ' ' + pad(date.getDate()) + '/' + pad(date.getMonth() + 1)
                + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes());
        },
        openException() {
            const start = new Date();
            start.setHours(16, 30, 0, 0);
            const end = new Date(start);
            end.setDate(end.getDate() + 1);
            end.setHours(6, 0, 0, 0);
            this.exceptionForm = { kind: 'CLOSED', scope: 'all', startsAt: start, endsAt: end, reason: '' };
            this.exceptionError = null;
            this.exceptionDialog = true;
        },
        async saveException() {
            const f = this.exceptionForm;
            const payload = { kind: f.kind, startsAt: apiDateTime(f.startsAt), endsAt: apiDateTime(f.endsAt), reason: f.reason };
            if (f.scope === 'all') payload.allSchedules = true;
            else if (String(f.scope).startsWith('s:')) payload.scheduleId = Number(f.scope.slice(2));
            else if (String(f.scope).startsWith('u:')) payload.userId = Number(f.scope.slice(2));
            this.savingException = true;
            const response = await httpRequest.saveScheduleException(payload);
            this.savingException = false;
            if (response.status === 200) {
                this.apply(response.data);
                this.exceptionDialog = false;
                this.$toast.add({ severity: 'success', summary: 'Excepción guardada', detail: this.pushNote(response.data.push), life: 3000 });
            } else if (response.status === 422 && response.error) {
                this.exceptionError = response.error;
            } else {
                this.fail(response);
            }
        },
        confirmDeleteException(exception) {
            this.$confirm.require({
                header: 'Quitar excepción',
                message: (exception.kind === 'CLOSED' ? 'El cierre' : 'La habilitación') + ' de ' + exception.startsAtLabel
                    + ' a ' + exception.endsAtLabel + ' deja de aplicar.',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Quitar',
                rejectLabel: 'Cancelar',
                acceptClass: 'p-button-danger',
                accept: () => this.removeException(exception),
            });
        },
        async removeException(exception) {
            const response = await httpRequest.deleteScheduleException(exception.id);
            if (response.status === 200) this.apply(response.data);
            else this.fail(response);
        },
    },
};
</script>

<style scoped>
.empty-state { display: flex; flex-direction: column; align-items: center; gap: 0.35rem; text-align: center; padding: 2.5rem 1rem; }
.empty-icon { width: 4rem; height: 4rem; border-radius: 50%; background: #eef2f8; color: var(--primary-color); display: flex; align-items: center; justify-content: center; font-size: 1.75rem; margin-bottom: 0.5rem; }
.empty-title { font-size: 1.1rem; font-weight: 600; color: var(--text-color); }

.tabs { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 0.25rem; border-bottom: 1px solid var(--surface-border); }
.tab { font: inherit; font-weight: 500; color: var(--text-color-secondary); background: transparent; border: 0; border-bottom: 2px solid transparent; padding: 0.65rem 0.9rem; margin-bottom: -1px; cursor: pointer; display: flex; align-items: center; gap: 0.5rem; }
.tab:hover:not(:disabled) { color: var(--primary-color); }
.tab:disabled { opacity: 0.5; cursor: not-allowed; }
.tab.active { color: var(--primary-color); font-weight: 600; border-bottom-color: var(--primary-color); }
.tab-new { color: var(--primary-color); }
.tab-tag { font-size: 0.7rem; font-weight: 700; color: var(--primary-color); background: #eef2f8; padding: 0.1rem 0.45rem; border-radius: 6px; }
.tab:focus-visible { outline: none; box-shadow: var(--focus-ring); }
.name-input { width: 18rem; max-width: 100%; }

.day-row { display: grid; grid-template-columns: 6.5rem 3.6rem minmax(0, 1fr); gap: 0.75rem; align-items: start; padding: 0.6rem 0; border-bottom: 1px solid var(--surface-border); }
.day-head { font-size: 0.75rem; font-weight: 600; letter-spacing: 0.05em; color: var(--text-color-secondary); padding-top: 0; }
.day-name { font-weight: 600; color: var(--text-color); padding-top: 0.45rem; }
.shifts { display: flex; flex-direction: column; gap: 0.5rem; min-width: 0; }
.closed-day { display: flex; flex-direction: column; gap: 0.1rem; padding-top: 0.45rem; }
.shift { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; }
:deep(.time-input) { width: 5.2rem; font-variant-numeric: tabular-nums; text-align: center; }
.shift-note { font-size: 0.75rem; font-weight: 600; color: var(--primary-color); background: #eef2f8; padding: 0.2rem 0.5rem; border-radius: 6px; }
.add-shift { align-self: flex-start; }

.now-status { display: inline-flex; align-items: center; gap: 0.45rem; font-size: 0.875rem; font-weight: 600; color: var(--text-color); }
.dot { width: 0.55rem; height: 0.55rem; border-radius: 50%; flex: none; background: #6c757d; }
.dot-open { background: #15803d; }
.dot-grace { background: #b45309; }
.dot-free { background: #1d4ed8; }

.week-row { display: grid; grid-template-columns: 5.6rem minmax(0, 1fr); gap: 0.25rem 0.6rem; align-items: start; margin-bottom: 0.45rem; }
.week-day { font-weight: 600; color: var(--text-color); padding-top: 0.2rem; }
.week-day.today { color: var(--primary-color); font-weight: 700; }
.axis { position: relative; height: 1rem; font-size: 0.7rem; color: var(--text-color-secondary); font-variant-numeric: tabular-nums; }
.axis span { position: absolute; transform: translateX(-50%); }
.axis span:first-child, .axis span:last-child { transform: none; }
.track { position: relative; height: 1.6rem; border-radius: 6px; background:
    linear-gradient(to right, transparent calc(25% - 1px), var(--surface-border) calc(25% - 1px), var(--surface-border) 25%, transparent 25%,
        transparent calc(50% - 1px), var(--surface-border) calc(50% - 1px), var(--surface-border) 50%, transparent 50%,
        transparent calc(75% - 1px), var(--surface-border) calc(75% - 1px), var(--surface-border) 75%, transparent 75%),
    var(--surface-hover); }
.seg { position: absolute; top: 3px; bottom: 3px; border-radius: 4px; }
.seg-shift { background: var(--primary-color); }
.seg-carry { background: repeating-linear-gradient(135deg, var(--primary-color) 0 4px, #7b93bd 4px 8px); }
.seg-grace { top: auto; height: 6px; bottom: 3px; background: #f18700; }
.now-line { position: absolute; top: -4px; bottom: -4px; width: 2px; background: var(--text-color); border-radius: 1px; }
.week-text { display: block; margin-top: 0.15rem; color: var(--text-color-secondary); font-variant-numeric: tabular-nums; }
.legend { display: flex; flex-wrap: wrap; gap: 0.5rem 1.1rem; margin-top: 0.4rem; font-size: 0.8rem; }
.legend > span { display: inline-flex; align-items: center; gap: 0.4rem; }
.sw { display: inline-block; width: 1.1rem; height: 0.6rem; border-radius: 3px; }
.sw-shift { background: var(--primary-color); }
.sw-carry { background: repeating-linear-gradient(135deg, var(--primary-color) 0 4px, #7b93bd 4px 8px); }
.sw-grace { height: 0.35rem; background: #f18700; }
.sw-now { width: 2px; height: 0.9rem; background: var(--text-color); }

.editor-footer { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 1rem; border-top: 1px solid var(--surface-border); padding-top: 1rem; margin-top: 1rem; }
:deep(.grace-input) { width: 6rem; }

.exception-preview { display: flex; align-items: center; gap: 0.6rem; padding: 0.75rem 0.9rem; border-radius: 8px; background: var(--surface-hover); border: 1px solid var(--surface-border); color: var(--text-color); margin-bottom: 0.5rem; }
</style>
